import { apiRoutes } from 'config/apiRoutes'
import axios from 'libs/fetch'
import { useMutation, useQueryClient } from 'libs/query'
import { useTranslation } from 'libs/i18n'
import { useToast } from 'shared/hooks'
import { queryKeys } from 'config/queryKeys'
import type { Booking } from 'features/bookings/types/bookings.types'

type UseMarkAsCompletedProps = {
	id: number | undefined
	closeModal: () => void
}

export const useMarkAsCompleted = ({ id, closeModal }: UseMarkAsCompletedProps) => {
	const queryClient = useQueryClient()
	const { t } = useTranslation()

	const { mutate: markAsCompletedMutation } = useMutation({
		mutationFn: (id: number) =>
			axios.post<Booking>(apiRoutes.bookingMarkAsCompleted(id.toString())),
		onSuccess: ({ data }) => {
			useToast({
				type: 'success',
				message: t('Booking marked as completed')
			})()

			const result = data?.completion_payout_result
			const payout = data?.completion_payout

			if (result?.code === 'created' && payout) {
				useToast({
					type: 'success',
					message: t('Payout #{{ id }} prepared', { id: payout.ref_nr })
				})()
			} else if (result?.code === 'nothing_to_pay') {
				useToast({ type: 'info', message: t('No payout needed') })()
			} else if (result) {
				useToast({
					type: 'warning',
					message: t('Payout could not be created: {{ detail }}', { detail: result.detail })
				})()
			}

			if (id) {
				queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(id.toString()) })
			}
			closeModal()
		},
		onError: (err: any) => {
			useToast({
				type: 'error',
				message: err.response?.data?.detail ?? t('Something went wrong')
			})()
		}
	})

	return {
		markAsCompletedMutation
	}
}
